import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Hexagon } from "lucide-react";
import { ledgerApi } from "../lib/ledgerApi";

/**
 * Subtle "Verified by ⌈2/3⌉ federation" pill.
 *
 * Two ways to feed it:
 *   1. Pass `federation` directly (from /api/x402/verify-receipt response or
 *      the equivalent shape we synthesize from a block).
 *   2. Pass `blockNumber` and we fetch + memoize the block. Renders nothing
 *      while loading or if blockNumber is null/undefined.
 *
 * Legacy blocks render the "Single sealer (legacy)" variant.
 */
export default function FederationBadge({ federation, blockNumber, txId, size = "sm", testid }) {
  const [fetched, setFetched] = useState(null);
  useEffect(() => {
    if (federation || blockNumber == null) return;
    let alive = true;
    ledgerApi
      .getBlock(blockNumber)
      .then((b) => {
        if (!alive) return;
        setFetched({
          legacy_single_sealer: !!b.legacy_single_sealer,
          block_number: b.block_number,
          block_hash: b.block_hash,
          proposer_name: b.proposer_name,
          threshold: b.consensus_threshold,
          total_weight: b.consensus_total_weight,
          valid_weight: b.consensus_valid_weight,
          signers: (b.validator_signatures || []).map((s) => ({
            validator_name: s.validator_name,
            valid: !!s.valid,
            byzantine_flag: !!s.byzantine_flag,
          })),
        });
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [federation, blockNumber]);
  const f = federation || fetched;
  if (!f) return null;
  const legacy = !!f.legacy_single_sealer;
  const valid = f.valid_weight ?? f.threshold ?? 0;
  const total = f.total_weight ?? f.threshold ?? 0;

  const base =
    "inline-flex items-center gap-1.5 border rounded-sm font-mono uppercase tracking-widest";
  const pad = size === "lg" ? "px-2.5 py-1 text-[10px]" : "px-1.5 py-0.5 text-[9px]";
  const tone = legacy
    ? "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300"
    : "border-status-ok/40 bg-status-ok/10 text-status-ok";

  const tooltip = legacy
    ? "This block predates the validator federation. Trust root: single network sealer key (Phase 0–5). Inclusion proofs still verifiable."
    : `Block ${f.block_number ?? ""} signed by ⌈${f.threshold ?? "?"}⌉/${f.total_weight ?? "?"} validators` +
      (f.signers?.length
        ? `: ${f.signers
            .filter((s) => s.valid)
            .map((s) => s.validator_name)
            .join(", ")}`
        : "");

  const inner = (
    <span data-testid={testid || "federation-badge"} className={`${base} ${pad} ${tone}`} title={tooltip}>
      {legacy ? (
        <>
          <Hexagon className="w-3 h-3" strokeWidth={1.5} />
          single sealer · legacy
        </>
      ) : (
        <>
          <ShieldCheck className="w-3 h-3" strokeWidth={1.5} />
          verified · {valid}/{total}
        </>
      )}
    </span>
  );

  // If we have a tx_id, link the badge to the public proof page.
  if (txId) {
    return (
      <Link to={`/tx/${txId}`} className="hover:opacity-80 transition-opacity">
        {inner}
      </Link>
    );
  }
  return inner;
}
